// Email + phone ownership proofs for the signed-in user.
// Every decision (verified / gated) is returned by the server; nothing here is
// self-asserted, and phone stays optional until a high-risk action needs it.
import React, { useCallback, useEffect, useState } from 'react';
import { MailCheck, MailWarning, PhoneCall, ShieldCheck } from 'lucide-react';
import { verification } from '@/lib/accountSecurity';

const VerifyContactPanel: React.FC = () => {
  const [s, setS] = useState<any>(null);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    await verification.syncEmail();
    const { data } = await verification.status();
    setS(data);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<{ error: string | null }>, ok: string) => {
    setBusy(true);
    const { error } = await fn();
    setMsg(error ? errText(error) : ok);
    if (!error) await load();
    setBusy(false);
  };

  const errText = (e: string) =>
    e === 'rate_limited'
      ? 'Too many attempts. Please wait before trying again.'
      : e === 'invalid_code'
      ? 'That code did not match.'
      : e === 'phone_already_verified_elsewhere'
      ? 'This number is already verified on another account.'
      : e === 'verification_unavailable'
      ? 'SMS verification is not configured yet.'
      : e;

  const emailVerified = s?.email_verified;
  const phoneVerified = s?.phone_verified;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
        <ShieldCheck className="h-5 w-5 text-slate-700" /> Account verification
      </h3>

      <div className="mt-4 rounded-xl border border-slate-200 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          {emailVerified ? <MailCheck className="h-4 w-4 text-emerald-600" /> : <MailWarning className="h-4 w-4 text-amber-600" />}
          Email {emailVerified ? 'verified' : s?.email_verification_state === 'legacy_unverified' ? 'not independently verified' : 'unverified'}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {emailVerified
            ? `${s?.email} — mailbox ownership proven.`
            : 'Mailbox ownership has not been proven yet. Send yourself a one-time verification link (valid 24 hours) and open it from your inbox — it works on any device.'}
        </p>
        {!emailVerified && (
          <button
            onClick={() => act(verification.resendEmail, 'Verification link sent. Open it from your inbox within 24 hours.')}
            disabled={busy}
            className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            Send verification link
          </button>
        )}
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <PhoneCall className={`h-4 w-4 ${phoneVerified ? 'text-emerald-600' : 'text-slate-500'}`} />
          Phone {phoneVerified ? `verified (${s?.phone_masked})` : 'not verified'}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {phoneVerified
            ? 'Lead purchases, high-volume posting and payment-identity changes are unlocked.'
            : 'Browsing never requires a phone number. A verified number is required before purchasing or receiving leads, posting more than 3 projects in 24 hours, receiving promotional credits or changing payment identity.'}
        </p>
        {!phoneVerified && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="tel"
              placeholder="(555) 123-4567"
              className="w-48 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              onClick={() => act(() => verification.phoneStart(phone), 'Code sent by SMS.').then(() => setSent(true))}
              disabled={busy || phone.replace(/\D/g, '').length < 10}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Send code
            </button>
            {sent && (
              <>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  placeholder="6-digit code"
                  className="w-36 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                  onClick={() => act(() => verification.phoneCheck(phone, code), 'Phone number verified.')}
                  disabled={busy || code.trim().length < 4}
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  Confirm
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {msg && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{msg}</p>}
    </section>
  );
};

export default VerifyContactPanel;
